// @ts-strict-ignore
import React, { useContext, useEffect, useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import {
  Box,
  Button,
  Callout,
  Combobox,
  Box as Form,
  Input,
  toast,
} from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import { useCreateWalletMutation } from 'hooks/mutations'
import { useSupportedCurrenciesQuery } from 'hooks/queries'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useSupportedCurrencies } from 'hooks/useSupportedCurrencies'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { createWalletSchema } from 'pages/Wallets/schemas/createWalletSchema'
import { ErrorCodes } from 'types/errorCodes'
import { WalletsCreateRequest } from 'types/wallets'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

export const WalletCreateForm = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [walletAction] = useContext(WalletActionContext)
  const { enableRestrictDepositCurrencyToCrypto } = useFeatureFlags()
  const { getSupportedCurrencies, getCurrencyOptions } = useSupportedCurrencies()
  const { isPending, mutateAsync: createWallet } = useCreateWalletMutation()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const { data: currencies, isPending: isSupportedCurrenciesLoading } = useSupportedCurrenciesQuery(
    enableRestrictDepositCurrencyToCrypto
  )
  const { register, setValue, handleSubmit, formState, getValues } = useForm<WalletsCreateRequest>({
    defaultValues: {
      currency: '',
      description: '',
    },
    resolver: yupResolver(createWalletSchema(t)),
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const currenciesOptions = useMemo(() => {
    const supportedCurrencies = getSupportedCurrencies(currencies)
    return getCurrencyOptions(supportedCurrencies)
  }, [currencies])

  const { onChange, ...restCurrencyProps } = register('currency')

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.WALLET_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const onSubmit: SubmitHandler<WalletsCreateRequest> = async payload => {
    resetRequestError()
    try {
      await createWallet(payload)

      track.Amp.track(AmplitudeEvent.EOF_INITIATE_WALLET_SUCCESS, {
        category: AmplitudeEventCategory.WALLET,
        action: AmplitudeEventAction.CLICK,
        currency: getValues('currency'),
      })

      toast({
        header: t('walletCreated', {
          walletName: payload.description,
        }),
        variant: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['walletsV2'] })
      walletAction?.onClose()
    } catch (error) {
      if (error?.errorList?.[0]?.code === ErrorCodes.CURRENCY_NOT_SUPPORTED) {
        setRequestError({
          show: true,
          errorCode: error?.status,
          handledMessage: (
            <>
              {t('currencyNotSupported')}{' '}
              <ExternalLink href={process.env.CURRENCY_NOT_SUPPORTED}> {t('here')}</ExternalLink>
            </>
          ),
        })
        return
      }
      setRequestError({
        show: true,
        errorCode: error?.status,
        handledMessage: error?.data?.errorList?.[0]?.message || t('oopsSomethingWentWrong'),
      })
      logSentryError('Error: useCreateWalletMutation', error)
    }
  }

  return (
    <Form
      flexCol
      width="full"
      tag="form"
      testId="wallet-create-form"
      gapY={16}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Combobox
        id="currency"
        label={t('selectCurrency')}
        testId="wallet-currency-select"
        name="currency"
        items={currenciesOptions}
        onChange={value => {
          setValue('currency', value)
        }}
        loading={isSupportedCurrenciesLoading}
        {...restCurrencyProps}
      />
      <Input
        label={t('walletName')}
        inputMode="text"
        placeholder="Ie. ETH Wallet"
        name="description"
        testId="wallet-name"
        error={!!formState.errors?.description}
        errorText={formState.errors?.description?.message}
        {...register('description')}
        onChange={e => {
          setValue('description', e?.target?.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }}
      />
      {enableRestrictDepositCurrencyToCrypto && (
        <Callout testId="wallet-info-callout" variant="info">
          {t('createFiatWalletDisclaimer')}
          <ExternalLink href="https://help.bvnk.com/hc/en-us/articles/13995679766930">
            {` ${t('learnMore')}`}
          </ExternalLink>
        </Callout>
      )}

      {requestError?.show && <RequestErrorCallout requestError={requestError} />}

      <Box flex justify="between" paddingT={16}>
        <Button testId="cancel-create-wallet" variant="secondary" onClick={walletAction?.onClose}>
          {t('cancel')}
        </Button>
        <Button
          testId="create-wallet"
          type="submit"
          variant="primary"
          loading={isPending}
          disabled={!formState.isValid}
        >
          {t('createWallet')}
        </Button>
      </Box>
    </Form>
  )
}
